import {
	type DraftVerificationAttachment,
	QuantityUnit,
	type VerificationAttachment,
} from '@api-client/api'
import { ANSWERED_UNIT_ADORNMENT } from '@components/controllers/jsonforms/AnsweredUnitContext'
import {
	evalRule,
	getAtPath,
	scopeToPath,
} from '@components/controllers/jsonforms/cleanHiddenFields'
import {
	PACT_UNIT_LABEL,
	type PactDeclaredUnit,
} from '@components/controllers/jsonforms/DeclaredUnitContext'
import {
	I18nContext,
	type TranslateFn,
} from '@components/controllers/jsonforms/I18nContext'
import { InfoKeyValue } from '@components/InfoKeyValue'
import type { FormDefinition } from '@components/JsonFormsRenderer/JsonFormsRenderer'
import {
	DetailsContainer,
	DetailsSpacer,
	DetailsSubContainer,
	DetailsSubtitle,
	DetailsTitle,
} from '@components/ProductRequestDetails/styles'
import type { JsonSchema, UISchemaElement } from '@jsonforms/core'
import { Box, Stack, Typography } from '@mui/material'
import { get } from 'lodash-es'
import { Fragment, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Matches the placeholder used by NumberRenderer for emission value fields.
const PLACEHOLDER_ADORNMENT = 'kgCO2eq / unit'

// Normalise unit strings: "kgCO2eq / kg" → "kgCO₂e/kg"
const formatUnit = (unit: string): string =>
	unit.replaceAll('CO2eq', 'CO\u2082e').replaceAll(' / ', '/')

// Exact unit definitions (NIST / SI). The pound and foot are legally defined as exact decimal
// values, so we use those rather than dividing by an approximation of the inverse.
const KG_IN_LB = 0.453_592_37
const LB_IN_OZ = 1 / 16
const M_IN_FT = 0.3048

// Conversion factor from each QuantityUnit to its category's canonical unit (kg / m\u00b3).
// Mirrors `QUANTITY_TO_CANONICAL` in `UnitConversionService.kt`.
const QUANTITY_TO_CANONICAL: Record<QuantityUnit, number> = {
	[QuantityUnit.G]: 0.001,
	[QuantityUnit.KG]: 1,
	[QuantityUnit.OZ]: KG_IN_LB * LB_IN_OZ,
	[QuantityUnit.LB]: KG_IN_LB,
	[QuantityUnit.ML]: 1e-6,
	[QuantityUnit.L]: 0.001,
	[QuantityUnit.M3]: 1,
	[QuantityUnit.FT3]: M_IN_FT * M_IN_FT * M_IN_FT,
}

const isKnownQuantityUnit = (value: string): value is QuantityUnit =>
	Object.hasOwn(QUANTITY_TO_CANONICAL, value)

const QUANTITY_UNIT_LABELS: Record<QuantityUnit, string> = {
	[QuantityUnit.G]: 'g',
	[QuantityUnit.KG]: 'kg',
	[QuantityUnit.OZ]: 'oz',
	[QuantityUnit.LB]: 'lb',
	[QuantityUnit.ML]: 'mL',
	[QuantityUnit.L]: 'L',
	[QuantityUnit.M3]: 'm\u00B3',
	[QuantityUnit.FT3]: 'ft\u00B3',
}

interface ParsedReportingUnit {
	value: number
	unit: QuantityUnit
}

const parseReportingUnit = (raw: unknown): ParsedReportingUnit | undefined => {
	if (!raw || typeof raw !== 'object') {
		return undefined
	}
	const obj = raw as Record<string, unknown>
	const value = obj.value
	const unit = obj.unit
	if (typeof value !== 'number' || !(value > 0)) {
		return undefined
	}
	if (typeof unit !== 'string' || !isKnownQuantityUnit(unit)) {
		return undefined
	}
	return { value, unit }
}

// Round to 4 significant digits for display of the reverse-derived per-item value.
const formatSignificantDigits = (value: number, digits = 4): string => {
	if (!Number.isFinite(value) || value === 0) {
		return String(value)
	}
	return Number(value.toPrecision(digits)).toString()
}

interface ReportingUnitConversionPanelProps {
	formData: Record<string, unknown>
}

/**
 * For PCFs that went through federation's per-item conversion (SUPE-570): show the
 * supplier's raw per-item answer alongside the derived per-canonical headline, so
 * carbon controllers can audit the math. Only meaningful on the customer-review
 * side, where `headlineValue` is the post-conversion per-canonical number; gated
 * by FormDataViewer's `showConversionPanel` prop (off on the supplier side where
 * the headline is still per-item and reverse-deriving would produce nonsense).
 */
const ReportingUnitConversionPanel = ({
	formData,
}: ReportingUnitConversionPanelProps) => {
	const { t } = useTranslation()

	const reportingUnit = parseReportingUnit(formData.reportingUnit)
	const headlineValue = get(formData, [
		'emissionValues',
		'emissionsValuesBreakdown',
		'valueExcludingBiogenicCarbon',
	])
	const answeredUnit = formData.answeredUnit

	if (!reportingUnit || typeof headlineValue !== 'number') {
		return null
	}

	const quantityInCanonical =
		reportingUnit.value * QUANTITY_TO_CANONICAL[reportingUnit.unit]
	const rawPerItem = headlineValue * quantityInCanonical

	const headlineUnitLabel =
		typeof answeredUnit === 'string'
			? formatUnit(
					ANSWERED_UNIT_ADORNMENT[
						answeredUnit as keyof typeof ANSWERED_UNIT_ADORNMENT
					] ?? 'kgCO2eq'
				)
			: 'kgCO\u2082e'
	const perItemLabel = `${formatSignificantDigits(rawPerItem)} ${formatUnit('kgCO2eq / item')}`
	const quantityLabel = `${reportingUnit.value} ${QUANTITY_UNIT_LABELS[reportingUnit.unit]}`
	const headlineLabel = `${headlineValue} ${headlineUnitLabel}`

	// Pick the derived-value label by reportingUnit category. Using static t() calls
	// (not a dynamic context arg) so extract-translations doesn't strip the variants.
	const isWeight =
		reportingUnit.unit === QuantityUnit.G ||
		reportingUnit.unit === QuantityUnit.KG ||
		reportingUnit.unit === QuantityUnit.OZ ||
		reportingUnit.unit === QuantityUnit.LB
	const derivedLabel = isWeight
		? t('emissionForm.reportingUnit.conversion.derivedValue_WEIGHT')
		: t('emissionForm.reportingUnit.conversion.derivedValue_VOLUME')

	// Show the quantity in its canonical unit (kg / m\u00B3) in the equation so unit cancellation is
	// visually obvious \u2014 supplier's typed unit is already shown in the row above.
	const canonicalQuantityLabel = `${formatSignificantDigits(quantityInCanonical)} ${isWeight ? 'kg' : 'm\u00B3'}`
	const equation = `${perItemLabel} \u00F7 ${canonicalQuantityLabel} = ${headlineLabel}`

	return (
		<DetailsSubContainer>
			<DetailsTitle>
				{t('emissionForm.reportingUnit.conversion.title')}
			</DetailsTitle>
			<Stack gap={2} sx={{ pt: 1 }}>
				<InfoKeyValue
					label={t('emissionForm.reportingUnit.conversion.requestedUnit')}
					value={headlineUnitLabel}
				/>
				<InfoKeyValue
					label={t('emissionForm.reportingUnit.conversion.supplierValue')}
					value={perItemLabel}
				/>
				<InfoKeyValue
					label={t('emissionForm.reportingUnit.conversion.quantityPerItem')}
					value={quantityLabel}
				/>
				<InfoKeyValue label={derivedLabel} value={equation} />
			</Stack>
		</DetailsSubContainer>
	)
}

interface FormDataViewerProps {
	formDefinition: FormDefinition
	formData: Record<string, unknown>
	onDownload?: (attachmentId: string) => void
	// Only set on the customer-review side, where federation has already converted
	// the supplier's per-item answer to a per-canonical headline. On the supplier
	// side the headline is still per-item and reverse-deriving it makes no sense.
	showConversionPanel?: boolean
}

/**
 * Navigate rootSchema following a JSONForms scope string.
 * e.g. "#/properties/emissionValues/properties/referenceYear" → the referenceYear sub-schema.
 */
const resolveSchemaByScope = (
	rootSchema: JsonSchema,
	scope: string
): JsonSchema => {
	const parts = scope.replace(/^#\//, '').split('/')
	let current: Record<string, unknown> = rootSchema as Record<string, unknown>
	for (const part of parts) {
		if (!current || typeof current !== 'object') {
			return {}
		}
		current = (current as Record<string, unknown>)[part] as Record<
			string,
			unknown
		>
	}
	return (current ?? {}) as JsonSchema
}

/**
 * Format a raw formData value for display.
 * Exported for unit testing.
 */
export const formatFieldValue = (
	value: unknown,
	fieldSchema: JsonSchema,
	options: Record<string, unknown> | undefined,
	path: string,
	translate: TranslateFn,
	t: (key: string) => string,
	formData: Record<string, unknown>
):
	| string
	| readonly (VerificationAttachment | DraftVerificationAttachment)[] => {
	if (value === undefined || value === null) {
		return '-'
	}

	// Attachment (options.format === 'attachment')
	if (options?.format === 'attachment') {
		const attachments = value as (
			| VerificationAttachment
			| DraftVerificationAttachment
		)[]
		return attachments.length === 0 ? '-' : attachments
	}

	// Boolean (includes checkbox format)
	if (typeof value === 'boolean') {
		return value ? t('yes') : t('no')
	}

	// Date
	const schemaFormat = fieldSchema.format
	if (
		typeof value === 'string' &&
		(schemaFormat === 'date' || schemaFormat === 'date-time')
	) {
		return new Date(value).toLocaleDateString()
	}

	// Enum / oneOf (includes radio and multi-select)
	const itemSchema =
		fieldSchema.type === 'array'
			? (fieldSchema.items as JsonSchema | undefined)
			: fieldSchema
	if (itemSchema?.oneOf || itemSchema?.enum) {
		const propertyName = path.split('.').at(-1) ?? path
		// Resolve title from oneOf items as fallback (matches SelectRenderer behaviour)
		const getTitleForValue = (v: string): string => {
			const oneOfItems = itemSchema?.oneOf as
				| { const?: unknown; title?: unknown }[]
				| undefined
			const item = oneOfItems?.find(i => String(i.const) === v)
			return (item?.title as string | undefined) ?? v
		}
		if (Array.isArray(value)) {
			return (value as string[])
				.map(
					v =>
						translate(`${propertyName}.options.${v}`, undefined) ||
						formatUnit(getTitleForValue(v))
				)
				.join(', ')
		}
		const strValue = String(value)
		return (
			translate(`${propertyName}.options.${strValue}`, undefined) ||
			formatUnit(getTitleForValue(strValue))
		)
	}

	// Number
	if (typeof value === 'number') {
		const endAdornment = options?.endAdornment as string | undefined
		if (endAdornment === '%') {
			return `${value}%`
		}
		if (endAdornment === PLACEHOLDER_ADORNMENT) {
			const answeredUnit = formData.answeredUnit as string | undefined
			const resolved = answeredUnit
				? ANSWERED_UNIT_ADORNMENT[
						answeredUnit as keyof typeof ANSWERED_UNIT_ADORNMENT
					]
				: undefined
			return `${value} ${formatUnit(resolved ?? endAdornment)}`
		}
		if (endAdornment?.includes('declaredUnit')) {
			const pcf = formData.pcf as Record<string, unknown> | undefined
			const rawUnit = pcf?.declaredUnit as string | undefined
			const unitLabel =
				(rawUnit ? PACT_UNIT_LABEL[rawUnit as PactDeclaredUnit] : undefined) ??
				rawUnit ??
				'declaredUnit'
			return `${value} ${formatUnit(endAdornment.replace('declaredUnit', unitLabel))}`
		}
		if (endAdornment) {
			return `${value} ${formatUnit(endAdornment)}`
		}
		return String(value)
	}

	return String(value)
}

/** Convert a camelCase or snake_case key to Title Case for display. */
const formatCustomFieldLabel = (key: string): string =>
	key
		.replaceAll('_', ' ')
		.replaceAll(/([A-Z])/g, ' $1')
		.replace(/^./, s => s.toUpperCase())
		.trim()

/** Format a scalar custom field value as a display string. */
const formatCustomFieldValue = (value: unknown): string => {
	if (value === null || value === undefined) {
		return '-'
	}
	if (typeof value === 'boolean') {
		// Fallback only — callers that have access to t() should translate booleans themselves.
		return value ? 'Yes' : 'No'
	}
	if (typeof value === 'number') {
		return String(value)
	}
	if (typeof value === 'string') {
		return value || '-'
	}
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return '-'
		}
		return value
			.map(item =>
				typeof item === 'object' && item !== null
					? JSON.stringify(item)
					: String(item ?? '-')
			)
			.join(', ')
	}
	return JSON.stringify(value)
}

/**
 * Renders a custom field entry. Nested objects are rendered recursively as indented sub-rows
 * rather than collapsed into a single string.
 */
const CustomFieldEntry = ({
	fieldKey,
	value,
	depth = 0,
}: {
	fieldKey: string
	value: unknown
	depth?: number
}) => {
	const { t } = useTranslation()
	const label = formatCustomFieldLabel(fieldKey)
	if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
		const entries = Object.entries(value as Record<string, unknown>)
		return (
			<>
				<Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
					{label}
				</Typography>
				<Box sx={{ pl: (depth + 1) * 2 }}>
					{entries.map(([k, v]) => (
						<CustomFieldEntry
							key={k}
							fieldKey={k}
							value={v}
							depth={depth + 1}
						/>
					))}
				</Box>
			</>
		)
	}
	if (
		Array.isArray(value) &&
		value.some(item => typeof item === 'object' && item !== null)
	) {
		return (
			<>
				<Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
					{label}
				</Typography>
				<Box sx={{ pl: (depth + 1) * 2 }}>
					{(value as unknown[]).map((item, index) => (
						<Fragment key={index}>
							{index > 0 && <DetailsSpacer />}
							{typeof item === 'object' && item !== null
								? Object.entries(item as Record<string, unknown>).map(
										([k, v]) => (
											<CustomFieldEntry
												key={k}
												fieldKey={k}
												value={v}
												depth={depth + 1}
											/>
										)
									)
								: String(item ?? '-')}
						</Fragment>
					))}
				</Box>
			</>
		)
	}
	const displayValue =
		typeof value === 'boolean'
			? value
				? t('yes')
				: t('no')
			: formatCustomFieldValue(value)
	return (
		<InfoKeyValue
			className="key-value-container"
			label={label}
			value={displayValue}
		/>
	)
}

// Fields that live in the supplier's draft formData but are internal metadata
// (supplier-only decisions about what to share) rather than customer-facing values.
const INTERNAL_HIDDEN_FIELDS = new Set(['breakdownSharingLevel'])

// Top-level keys we render through dedicated components (ReportingUnitConversionPanel etc.)
// rather than the generic custom-field fallback.
const TOP_LEVEL_DEDICATED_KEYS = new Set(['reportingUnit'])

// Lifecycle stage keys rendered by the EmissionsBreakdown UISchema element.
const BREAKDOWN_STAGE_KEYS = [
	'materialAcquisitionAndPreprocessing',
	'production',
	'distributionAndStorage',
	'productUse',
	'endOfLife',
] as const

type BreakdownStageKey = (typeof BREAKDOWN_STAGE_KEYS)[number]

// Resolves stage labels via static t() calls so the extract-translations hook
// reliably detects each translation key.
const getStageLabel = (
	t: (key: string) => string,
	stageKey: BreakdownStageKey
): string => {
	switch (stageKey) {
		case 'materialAcquisitionAndPreprocessing':
			return t('emissions.lifecycle.title_materialAcquisitionAndPreprocessing')
		case 'production':
			return t('emissions.lifecycle.title_production')
		case 'distributionAndStorage':
			return t('emissions.lifecycle.title_distributionAndStorage')
		case 'productUse':
			return t('emissions.lifecycle.title_productUse')
		case 'endOfLife':
			return t('emissions.lifecycle.title_endOfLife')
	}
}

/**
 * Collect all Control scope strings referenced in a UISchema element tree.
 * Used to detect custom fields that exist in formData but have no UISchema control.
 */
const collectScopesFromElements = (
	elements: UISchemaElement[]
): Set<string> => {
	const scopes = new Set<string>()
	const visit = (el: UISchemaElement): void => {
		const elAny = el as unknown as Record<string, unknown>
		if (
			el.type === 'Control' ||
			el.type === 'FieldArrayBlock' ||
			el.type === 'EmissionsBreakdown'
		) {
			const scope = elAny.scope as string | undefined
			if (scope) {
				scopes.add(scope)
			}
		}
		const children = (elAny.elements as UISchemaElement[] | undefined) ?? []
		for (const child of children) {
			visit(child)
		}
	}
	for (const element of elements) {
		visit(element)
	}
	return scopes
}

const renderElement = (
	el: UISchemaElement,
	data: Record<string, unknown>,
	rootSchema: JsonSchema,
	translate: TranslateFn,
	t: (key: string) => string,
	onDownload: ((id: string) => void) | undefined
): React.ReactNode => {
	if (!evalRule(el.rule, data)) {
		return null
	}

	const elAny = el as unknown as Record<string, unknown>

	if (el.type === 'Control') {
		const scope = elAny.scope as string
		if (!scope) {
			return null
		}
		const options = elAny.options as Record<string, unknown> | undefined
		const path = scopeToPath(scope)
		const fieldSchema = resolveSchemaByScope(rootSchema, scope)
		const value = getAtPath(data, path)
		const schemaTitle = (fieldSchema as { title?: string }).title
		const rawLabel =
			(elAny.label as string | undefined) ??
			schemaTitle ??
			path.split('.').at(-1) ??
			path
		// i18next-ignore-dynamic-key
		const label = translate(rawLabel, undefined) || t(rawLabel) || rawLabel
		const formatted = formatFieldValue(
			value,
			fieldSchema,
			options,
			path,
			translate,
			t,
			data
		)

		if (options?.format === 'attachment') {
			return (
				<InfoKeyValue
					className="key-value-container"
					label={label}
					value={
						formatted === '-'
							? '-'
							: (formatted as readonly (
									| VerificationAttachment
									| DraftVerificationAttachment
								)[])
					}
					action={onDownload}
				/>
			)
		}

		return (
			<InfoKeyValue
				className="key-value-container"
				label={label}
				value={formatted as string | number}
			/>
		)
	}

	if (el.type === 'Group') {
		const isFlat =
			(elAny.options as { flat?: boolean } | undefined)?.flat === true
		const groupLabel = elAny.label as string | undefined
		const groupElements = (elAny.elements as UISchemaElement[]) ?? []
		const children = groupElements.map((child, i) => (
			<Fragment key={i}>
				{renderElement(child, data, rootSchema, translate, t, onDownload)}
			</Fragment>
		))
		if (isFlat) {
			return children
		}
		return (
			<DetailsSubContainer>
				{groupLabel && (
					<DetailsTitle>
						{/* i18next-ignore-dynamic-key */}
						{translate(groupLabel, undefined) || t(groupLabel)}
					</DetailsTitle>
				)}
				{children}
			</DetailsSubContainer>
		)
	}

	if (el.type === 'VerticalLayout') {
		const layoutElements = (elAny.elements as UISchemaElement[]) ?? []
		return (
			<>
				{layoutElements.map((child, i) => (
					<Fragment key={i}>
						{renderElement(child, data, rootSchema, translate, t, onDownload)}
					</Fragment>
				))}
			</>
		)
	}

	if (el.type === 'HorizontalLayout') {
		const layoutElements = (elAny.elements as UISchemaElement[]) ?? []
		return (
			<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
				{layoutElements.map((child, i) => (
					<Box key={i} sx={{ flex: 1 }}>
						{renderElement(child, data, rootSchema, translate, t, onDownload)}
					</Box>
				))}
			</Box>
		)
	}

	if (el.type === 'FieldArrayBlock') {
		const scope = elAny.scope as string
		const fabElements = (elAny.elements as UISchemaElement[]) ?? []
		const arrayPath = scopeToPath(scope)
		const arraySchema = resolveSchemaByScope(rootSchema, scope)
		const itemSchema = (arraySchema.items as JsonSchema | undefined) ?? {}
		const items =
			(getAtPath(data, arrayPath) as Record<string, unknown>[] | undefined) ??
			[]
		return (
			<Box sx={{ mb: 3 }}>
				{items.map((item, index) => (
					<Box key={index} sx={{ pl: 2 }}>
						{index > 0 && <DetailsSpacer />}
						{fabElements.map((child, i) => (
							<Fragment key={i}>
								{renderElement(
									child,
									item,
									itemSchema,
									translate,
									t,
									onDownload
								)}
							</Fragment>
						))}
					</Box>
				))}
			</Box>
		)
	}

	if (el.type === 'EmissionsBreakdown') {
		const scope = elAny.scope as string
		if (!scope) {
			return null
		}
		const breakdownPath = scopeToPath(scope)
		const breakdown = getAtPath(data, breakdownPath) as
			| Record<string, unknown>
			| undefined
		if (!breakdown) {
			return null
		}

		const answeredUnit = data.answeredUnit as string | undefined
		const unitLabel = answeredUnit
			? (ANSWERED_UNIT_ADORNMENT[
					answeredUnit as keyof typeof ANSWERED_UNIT_ADORNMENT
				] ?? PLACEHOLDER_ADORNMENT)
			: PLACEHOLDER_ADORNMENT
		const formattedUnit = formatUnit(unitLabel)

		const stageNodes = BREAKDOWN_STAGE_KEYS.flatMap(key => {
			const items = (
				(breakdown[key] as Record<string, unknown>[] | undefined) ?? []
			).filter(item => item.value !== undefined && item.value !== null)
			if (items.length === 0) {
				return []
			}
			const stageLabel = getStageLabel(t, key)

			// AGGREGATED (or single-item) -> one row per stage with the stage label.
			if (items.length === 1 && !items[0].description) {
				return [
					<InfoKeyValue
						key={key}
						className="key-value-container"
						label={stageLabel}
						value={`${items[0].value} ${formattedUnit}`}
					/>,
				]
			}

			// DETAILED -> stage header + one row per item, using the per-item description
			// (falling back to a numbered stage label if the supplier sent no description).
			const detailRows = items.map((item, index) => (
				<InfoKeyValue
					key={`${key}-${index}`}
					className="key-value-container"
					label={
						(item.description as string | undefined) ||
						`${stageLabel} ${index + 1}`
					}
					value={`${item.value} ${formattedUnit}`}
				/>
			))
			const header = (
				<DetailsSubtitle key={`${key}-header`}>{stageLabel}</DetailsSubtitle>
			)
			return [header].concat(detailRows)
		})

		return stageNodes.length > 0 ? stageNodes : null
	}

	// Divider, FormLabel, Infobox, TemplateSelector, Page — skip
	return null
}

const FormDataViewer = ({
	formDefinition,
	formData,
	onDownload,
	showConversionPanel = false,
}: FormDataViewerProps) => {
	const { formSchema, uiSchema, translations } = formDefinition
	const { i18n, t } = useTranslation()

	const translate: TranslateFn = useCallback(
		(key: string, defaultMessage: string | undefined) =>
			translations[i18n.language]?.[key] ?? defaultMessage ?? '',
		[translations, i18n.language]
	)

	const i18nValue = useMemo(() => ({ translate }), [translate])

	const pageElements = useMemo(
		() =>
			(uiSchema as { elements?: UISchemaElement[] } | undefined)?.elements ??
			[],
		[uiSchema]
	)

	// Scopes covered by ANY page — used to distinguish truly custom fields
	// from standard fields that simply live on a different page.
	const allCoveredScopes = useMemo(
		() => collectScopesFromElements(pageElements),
		[pageElements]
	)

	return (
		<I18nContext.Provider value={i18nValue}>
			<Box sx={{ display: 'flex', gap: 2 }}>
				<Box sx={{ flexGrow: 10 }}>
					<DetailsContainer>
						{pageElements.length === 0 && (
							<DetailsSubContainer>
								{Object.entries(formData)
									.filter(([key]) => !TOP_LEVEL_DEDICATED_KEYS.has(key))
									.map(([key, value]) => (
										<CustomFieldEntry key={key} fieldKey={key} value={value} />
									))}
							</DetailsSubContainer>
						)}
						{showConversionPanel && (
							<ReportingUnitConversionPanel formData={formData} />
						)}
						{pageElements.map((page, pageIndex) => {
							if (!evalRule(page.rule, formData)) {
								return null
							}
							// Skip pages containing a ProductInfo element — product-header pages
							// whose content is already displayed elsewhere in the UI.
							// NOTE: this heuristic is specific to the emission survey and may not
							// generalise to other custom surveys. If needed, a dedicated uiSchema
							// page option (e.g. options.hideInViewer: true) could replace this.
							const pageAny = page as unknown as Record<string, unknown>
							const pageElems =
								(pageAny.elements as UISchemaElement[] | undefined) ?? []
							if (
								pageElems.some(
									el =>
										(el as unknown as Record<string, unknown>).type ===
										'ProductInfo'
								)
							) {
								return null
							}
							const sectionLabel = pageAny.label as string | undefined
							const sectionElements =
								(pageAny.elements as UISchemaElement[] | undefined) ?? []

							// Detect custom fields: keys present in formData sections that
							// are not covered by any page's UISchema controls.
							// We also restrict to sections referenced on this page so that
							// fields from other pages don't bleed into this section's output.
							const thisPageScopes = collectScopesFromElements(sectionElements)
							const thisPageSectionKeys = new Set(
								[...thisPageScopes]
									.map(s => s.match(/^#\/properties\/([^/]+)/)?.[1])
									.filter((k): k is string => k !== undefined)
							)
							const customFieldsBySection = Object.entries(formData).flatMap(
								([sectionKey, sectionValue]) => {
									if (!thisPageSectionKeys.has(sectionKey)) {
										return []
									}
									if (
										typeof sectionValue !== 'object' ||
										sectionValue === null ||
										Array.isArray(sectionValue)
									) {
										return []
									}
									const entries = Object.entries(
										sectionValue as Record<string, unknown>
									).filter(([fieldKey]) => {
										// Internal supplier-side fields that should not surface in the
										// customer-facing summary even though they exist in formData.
										if (INTERNAL_HIDDEN_FIELDS.has(fieldKey)) {
											return false
										}
										const prefix = `#/properties/${sectionKey}/properties/${fieldKey}`
										// Exclude if directly covered or if any child scope is covered
										// (handles nested objects like methodologyStandards).
										return (
											!allCoveredScopes.has(prefix) &&
											![...allCoveredScopes].some(s =>
												s.startsWith(prefix + '/')
											)
										)
									})
									if (entries.length === 0) {
										return []
									}
									return entries
								}
							)

							return (
								<DetailsSubContainer key={pageIndex}>
									{sectionLabel && (
										<Typography
											variant="h6"
											sx={{
												pt: 3,
												fontWeight: 600,
												borderBottom: 1,
												borderColor: 'divider',
												pb: 1,
											}}
										>
											{/* i18next-ignore-dynamic-key */}
											{translate(sectionLabel, undefined) || t(sectionLabel)}
										</Typography>
									)}
									{sectionElements.map((el, i) => (
										<Fragment key={i}>
											{renderElement(
												el,
												formData,
												formSchema as JsonSchema,
												translate,
												t,
												onDownload
											)}
										</Fragment>
									))}
									{customFieldsBySection.map(([fieldKey, value]) => (
										<CustomFieldEntry
											key={`${pageIndex}.${fieldKey as string}`}
											fieldKey={fieldKey as string}
											value={value}
										/>
									))}
								</DetailsSubContainer>
							)
						})}
					</DetailsContainer>
				</Box>
			</Box>
		</I18nContext.Provider>
	)
}

export { FormDataViewer }
